import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

import { sharedMemoryCache, type MemoryCacheSnapshot } from "./createMemoryCache";

const IDLE_SNAPSHOT: MemoryCacheSnapshot<never> = {
  entry: null,
  inFlight: false,
  error: null,
};

export interface UseCachedResourceParams<T> {
  /** `null` não busca e não apaga o que já estiver em outra chave. */
  cacheKey: string | null;
  fetcher: () => Promise<T>;
  /**
   * `false`: não dispara rede. Se a chave já tiver dado, devolve a memória.
   * Fechar uma seção não deve voltar isto para `false` se a próxima abertura
   * precisar só reler o cache — quem chama segura o latch (`hasOpened`).
   */
  enabled?: boolean;
}

export interface UseCachedResourceResult<T> {
  data: T | undefined;
  /** Verdadeiro só na primeira busca, quando ainda não há dado. */
  isLoading: boolean;
  error: unknown;
  /** Ignora a memória e busca de novo. Atualiza quem assina a mesma chave. */
  refresh: () => Promise<T | undefined>;
}

/**
 * Assina o cache de módulo. Vários componentes na mesma chave compartilham
 * uma única request em voo.
 */
export function useCachedResource<T>({
  cacheKey,
  fetcher,
  enabled = true,
}: UseCachedResourceParams<T>): UseCachedResourceResult<T> {
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const subscribe = useCallback(
    (listener: () => void) => sharedMemoryCache.subscribe(listener),
    [],
  );

  const getSnapshot = useCallback(
    () =>
      cacheKey ? sharedMemoryCache.getKeySnapshot<T>(cacheKey) : IDLE_SNAPSHOT,
    [cacheKey],
  );

  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const refresh = useCallback(async () => {
    if (!cacheKey) return undefined;
    return sharedMemoryCache.getOrFetch(cacheKey, () => fetcherRef.current(), {
      force: true,
    });
  }, [cacheKey]);

  useEffect(() => {
    if (!enabled || !cacheKey) return;
    const current = sharedMemoryCache.getKeySnapshot(cacheKey);
    if (current.entry || current.inFlight) return;
    void sharedMemoryCache.getOrFetch(cacheKey, () => fetcherRef.current()).catch(() => {
      // O erro fica no snapshot para a UI. Não relançar no efeito.
    });
  }, [cacheKey, enabled]);

  const waitingFirstLoad = Boolean(
    enabled &&
      cacheKey &&
      !snapshot.entry &&
      !snapshot.error &&
      !snapshot.inFlight,
  );

  return {
    data: snapshot.entry?.data,
    isLoading: Boolean(snapshot.inFlight && !snapshot.entry) || waitingFirstLoad,
    error: snapshot.error,
    refresh,
  };
}
